import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    Checkbox,
    FormControlLabel,
    Link,
    CircularProgress
} from '@mui/material';
import { formatClassTime } from '../utils/classModalHelpers';
import { registerGuestForClass } from '../api/classesApi';
import { getAffiliateTerms } from '../api/affiliateApi';

export default function GuestRegistrationModal({ open, onClose, classData, affiliateId, onSuccess }) {
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [additionalInfo, setAdditionalInfo] = useState('');
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showTerms, setShowTerms] = useState(false);
    const [termsData, setTermsData] = useState(null);
    const [loadingTerms, setLoadingTerms] = useState(false);

    useEffect(() => {
        if (open && affiliateId) {
            fetchTerms();
        }
    }, [open, affiliateId]);

    const fetchTerms = async () => {
        try {
            setLoadingTerms(true);
            const data = await getAffiliateTerms(affiliateId);
            setTermsData(data || { terms: 'No terms available for this affiliate.' });
        } catch (error) {
            console.error("Error fetching affiliate terms:", error);
            setTermsData({ terms: 'Error loading terms. Please try again later.' });
        } finally {
            setLoadingTerms(false);
        }
    };

    const handleSubmit = async () => {
        // Reset error
        setError('');

        // Validation
        if (!firstName.trim() || !lastName.trim() || !email.trim()) {
            setError('All fields are required');
            return;
        }

        if (!termsAccepted) {
            setError('You must accept the terms and conditions');
            return;
        }

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setError('Please enter a valid email address');
            return;
        }

        setLoading(true);

        try {
            await registerGuestForClass(
                classData.id,
                firstName.trim(),
                lastName.trim(),
                email.trim(),
                affiliateId,
                additionalInfo.trim()
            );

            // Success
            if (onSuccess) {
                onSuccess();
            }
            handleClose();
        } catch (error) {
            const errorMessage = error.response?.data?.error || 'Failed to register for class';
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setFirstName('');
        setLastName('');
        setEmail('');
        setAdditionalInfo('');
        setTermsAccepted(false);
        setError('');
        setShowTerms(false);
        onClose();
    };

    const renderTermsContent = (text) => {
        if (!text) return null;

        const lines = text.split('\n');

        return (
            <Box sx={{ fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif', lineHeight: 1.6 }}>
                {lines.map((line, index) => {
                    if (line.trim() === '') {
                        return <Box key={index} sx={{ height: '12px' }} />;
                    }
                    return (
                        <Typography key={index} sx={{ mb: 1 }}>
                            {line}
                        </Typography>
                    );
                })}
            </Box>
        );
    };

    return (
        <>
            <Dialog open={open && !showTerms} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>
                    Register for Free Class
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        {classData && (
                            <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                                <Typography variant="h6" gutterBottom>
                                    {classData.trainingName}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {formatClassTime(classData.time)}
                                </Typography>
                            </Box>
                        )}

                        <TextField
                            fullWidth
                            label="First Name"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            margin="normal"
                            required
                            error={error && !firstName.trim()}
                        />

                        <TextField
                            fullWidth
                            label="Last Name"
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            margin="normal"
                            required
                            error={error && !lastName.trim()}
                        />

                        <TextField
                            fullWidth
                            label="Email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            margin="normal"
                            required
                            error={error && !email.trim()}
                        />

                        <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" sx={{mb: 0.5}}>
                                Additional Information (Optional)
                            </Typography>
                            <Typography variant="caption" sx={{mb: 1, color: 'text.secondary', display: 'block'}}>
                                This information will be sent to the club
                            </Typography>
                            <Box
                                component="textarea"
                                value={additionalInfo}
                                onChange={(e) => setAdditionalInfo(e.target.value)}
                                placeholder="Any previous sports background, injuries, or information to share with the club..."
                                sx={{
                                    width: '100%',
                                    minHeight: '60px',
                                    padding: '10px',
                                    border: '1px solid',
                                    borderColor: 'rgba(0, 0, 0, 0.23)',
                                    borderRadius: '4px',
                                    fontSize: '14px',
                                    fontFamily: 'inherit',
                                    resize: 'vertical',
                                    '&:focus': {
                                        outline: 'none',
                                        borderColor: 'primary.main',
                                        borderWidth: '2px'
                                    },
                                    '&:hover': {
                                        borderColor: 'rgba(0, 0, 0, 0.87)'
                                    }
                                }}
                            />
                        </Box>

                        <Box sx={{ mt: 2 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={termsAccepted}
                                        onChange={(e) => setTermsAccepted(e.target.checked)}
                                    />
                                }
                                label={
                                    <Typography variant="body2">
                                        I have read and agree to the{' '}
                                        <Link
                                            component="button"
                                            variant="body2"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                setShowTerms(true);
                                            }}
                                            sx={{ cursor: 'pointer' }}
                                        >
                                            Terms & Conditions
                                        </Link>
                                    </Typography>
                                }
                            />
                        </Box>

                        {error && (
                            <Typography color="error" sx={{ mt: 2 }}>
                                {error}
                            </Typography>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} disabled={loading}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        disabled={loading || !firstName.trim() || !lastName.trim() || !email.trim() || !termsAccepted}
                    >
                        {loading ? <CircularProgress size={20} color="inherit" /> : 'Register'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Terms Modal */}
            <Dialog open={showTerms} onClose={() => setShowTerms(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    Terms & Conditions
                </DialogTitle>
                <DialogContent dividers>
                    {loadingTerms ? (
                        <Box display="flex" justifyContent="center" p={2}>
                            <CircularProgress />
                        </Box>
                    ) : termsData ? (
                        renderTermsContent(termsData.terms)
                    ) : (
                        <Typography>No terms found.</Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowTerms(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

